import { useCallback, useSyncExternalStore } from "react";

// The cmehostinghealth event says whether Mux is *receiving* the feed - it says nothing
// about whether the HLS link viewers watch is actually serving advancing video. This
// probes the manifest behind the session stream URL: a live media playlist only hands
// out new sequence numbers/segments while video is going out, and a finished stream
// carries #EXT-X-ENDLIST.
//
// The URL behind session.data.streaming is normally the MASTER (multivariant) playlist,
// which carries no media sequence at all - it is followed once to the variant playlist
// that actually holds the live timeline.
//
// A host that does not send CORS headers fails closed (reported as "unreachable"), since
// an unreachable manifest is not something viewers can play either.
//
// Production cadence: one full cycle every 30 seconds. Each cycle proves four things in
// order - the master resolves, the variant playlist is valid/live, its timeline advances,
// and the CDN actually serves bytes of the newest segment - and only then is the stream
// reported as confirmed working (with the time it was confirmed at).
const PROBE_MS = 30000; // one full check every 30 seconds
const FAIL_LIMIT = 2; // two failed probes before "not playing" - one blip is not an outage
const STALL_LIMIT = 2; // two unchanged samples = the playlist stopped advancing
const CONTENT_FAIL_LIMIT = 2; // two cycles without served bytes = "no content"
const MAX_VARIANT_HOPS = 3;
const MAX_MASTER_FALLBACKS = 2;
const MEDIA_SEQ_RE = /#EXT-X-MEDIA-SEQUENCE:(\d+)/;

// One-shot force check (Go Live): how long to wait between samples and how many to take.
// 2 gaps of 1.5s = at most 3s of "Working..." before it must answer.
const FORCE_GAP_MS = 1500;
const FORCE_SAMPLES = 3;

function manifestLines(text) {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

// Highest-bandwidth variant of a master playlist (first one when no BANDWIDTH tag).
function pickVariant(lines) {
  let bestUrl = null;
  let bestBandwidth = -1;
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].startsWith("#EXT-X-STREAM-INF")) continue;
    const url = lines[i + 1];
    if (!url || url.startsWith("#")) continue;
    const bandwidth = Number(/BANDWIDTH=(\d+)/.exec(lines[i])?.[1] ?? NaN);
    if (bestUrl == null || (Number.isFinite(bandwidth) && bandwidth > bestBandwidth)) {
      bestUrl = url;
      bestBandwidth = Number.isFinite(bandwidth) ? bandwidth : bestBandwidth;
    }
  }
  return bestUrl;
}

// Pure part of the probe: classify one manifest body against the previously seen sample.
// Returns { status, nextUrl?, seq?, last? } where status is one of:
//   master     - multivariant playlist, no timeline; nextUrl is the variant to follow
//   ended      - the playlist is closed (the stream was published to its end)
//   invalid    - not an HLS manifest, or a playlist with no segments
//   sample     - first look at this playlist, nothing to compare against yet
//   advancing  - a new sequence number or segment since the last probe (video going out)
//   stalled    - unchanged since the last probe
export function readManifestState(text, prev = null) {
  if (typeof text !== "string" || !text.includes("#EXTM3U")) return { status: "invalid" };

  const lines = manifestLines(text);
  if (lines.some((l) => l.startsWith("#EXT-X-STREAM-INF"))) {
    const nextUrl = pickVariant(lines);
    return nextUrl ? { status: "master", nextUrl } : { status: "invalid" };
  }

  if (lines.includes("#EXT-X-ENDLIST")) return { status: "ended" };

  const seqMatch = MEDIA_SEQ_RE.exec(text);
  // HLS: an absent EXT-X-MEDIA-SEQUENCE implies sequence 0.
  const seq = seqMatch ? Number(seqMatch[1]) : 0;
  const last = [...lines].reverse().find((l) => !l.startsWith("#")) || "";
  if (!last) return { status: "invalid" }; // manifest without a single segment

  if (!prev) return { status: "sample", seq, last };
  const advanced = seq > prev.seq || last !== prev.last;
  return { status: advanced ? "advancing" : "stalled", seq, last };
}

const REASONS = {
  "no-url": "No stream url to probe",
  checking: "Waiting for the first probe",
  advancing: "Manifest is advancing",
  ended: "Stream ended",
  stalled: "Playlist not advancing",
  unreachable: "Manifest unreachable",
  "invalid-manifest": "Not a playable HLS manifest",
  "no-content": "No video content served",
};

export function playbackReasonText(reason) {
  return REASONS[reason] || reason || "";
}

// Freshness of every hop: the check is worthless if it can be answered from a cached
// copy. no-store keeps the browser out of the way, no-cache makes the CDN revalidate
// with the origin so we read the playlist the stream is publishing RIGHT NOW instead of
// one it still has in cache. (Both Mux hosts answer the preflight for these headers with
// Access-Control-Allow-Headers: * - verified.)
const FRESH_OPTS = {
  cache: "no-store",
  headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
};

// Fresh body of a URL, or null when it cannot be read (404/403/network/CORS).
async function fetchText(url) {
  try {
    const res = await fetch(url, FRESH_OPTS);
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

// The manifest can look perfect while the CDN serves nothing playable (broken origin,
// zero-length segments). A 1kB range request on the newest segment settles it: real bytes
// coming back is the difference between "the playlist looks fine" and "this URL delivers
// video". Range keeps it ~1kB/cycle instead of a whole segment download.
async function segmentHasContent(segmentUrl, baseUrl) {
  let url;
  try {
    url = new URL(segmentUrl, baseUrl).href;
  } catch {
    return false;
  }
  try {
    const res = await fetch(url, {
      ...FRESH_OPTS,
      headers: { ...FRESH_OPTS.headers, Range: "bytes=0-1023" },
    });
    if (!res.ok) return false;
    const declared = res.headers?.get?.("content-length");
    if (declared != null && declared !== "") {
      const bytes = Number(declared);
      if (Number.isFinite(bytes)) return bytes > 0;
    }
    if (typeof res.text !== "function") return true;
    const body = await res.text();
    return body.length > 0;
  } catch {
    return false;
  }
}

// One-shot force check for the Go Live gate: the same proof a playback cycle gives -
// fresh manifest, live media playlist, real bytes of the newest segment - plus an actual
// second sample a moment later, so "playing" means NEW video is arriving rather than an
// old-but-well-formed playlist being readable. Worst case wait: (maxSamples - 1) * gapMs.
// Returns { playing, reason } with the same reason vocabulary as the hook below.
export async function probeStreamNow(
  src,
  { gapMs = FORCE_GAP_MS, maxSamples = FORCE_SAMPLES } = {},
) {
  if (!src) return { playing: false, reason: "no-url" };
  try {
    let url = src;
    for (let hop = 0; hop <= MAX_VARIANT_HOPS; hop++) {
      const text = await fetchText(url);
      if (text == null) return { playing: false, reason: "unreachable" };

      const first = readManifestState(text, null);
      if (first.status === "master") {
        url = new URL(first.nextUrl, url).href;
        continue;
      }
      if (first.status === "ended") return { playing: false, reason: "ended" };
      if (first.status === "invalid") return { playing: false, reason: "invalid-manifest" };

      // A media playlist with at least one segment - now prove it serves video...
      if (!(await segmentHasContent(first.last, url))) {
        return { playing: false, reason: "no-content" };
      }

      // ...and that the NEXT sample has moved on (live, still being published).
      let prev = { seq: first.seq, last: first.last };
      for (let sample = 1; sample < maxSamples; sample++) {
        await new Promise((resolve) => {
          setTimeout(resolve, gapMs);
        });
        const nextText = await fetchText(url);
        if (nextText == null) return { playing: false, reason: "unreachable" };
        const parsed = readManifestState(nextText, prev);
        if (parsed.status === "ended") return { playing: false, reason: "ended" };
        if (parsed.status === "invalid") return { playing: false, reason: "invalid-manifest" };
        if (parsed.status === "advancing") return { playing: true, reason: "advancing" };
        prev = { seq: parsed.seq, last: parsed.last };
      }
      return { playing: false, reason: "stalled" };
    }
    return { playing: false, reason: "invalid-manifest" };
  } catch {
    return { playing: false, reason: "unreachable" };
  }
}

// ---------------------------------------------------------------------------
// Shared playing status - ONE source of truth per stream url.
//
// The Stream Health card (MainPage) and Go Live's playing check (StreamingVendorControls)
// read and write the same entry, so a url is never probed
// twice for the same answer and every section shows the same verdict the moment it
// changes:
//   - useStreamPlayback(url) subscribes. The first subscriber starts the 30s monitor for
//     that url, the last one to leave stops it - one monitor per url, however many
//     components show its status.
//   - checkStreamPlaying(url) is the on-demand check: it answers from a current "Yes"
//     without touching the network, otherwise runs the force probe (one at a time per
//     url) and publishes the result, which every subscriber shows at once.
//
// Entry state: { playing, reason, confirmedAt } - playing: true = the whole proof passed
// (playlist advancing AND real segment bytes served), false = ended / stalled /
// unreachable / no-content, null = still checking. confirmedAt is the epoch ms of the last
// confirmation - what the "Streaming Checked · hh:mm:ss" line is timestamped with.
// ---------------------------------------------------------------------------

const NO_URL = Object.freeze({ playing: null, reason: "no-url", confirmedAt: null });
const checkingState = (confirmedAt = null) => ({ playing: null, reason: "checking", confirmedAt });

// A "Yes" is trusted without a new check while it is this recent. The monitor confirms
// every cycle and keeps a Yes through one unchanged sample, so a Yes it still shows is at
// most two cycles old; anything older is a leftover, not the stream's current state.
const TRUST_YES_MS = 2 * PROBE_MS + 5000;

const entries = new Map(); // url -> { state, updatedAt, listeners, stopMonitor, forceCheck }

function entryFor(url) {
  let entry = entries.get(url);
  if (!entry) {
    entry = { state: checkingState(), updatedAt: Date.now(), listeners: new Set(), stopMonitor: null, forceCheck: null };
    entries.set(url, entry);
  }
  return entry;
}

function publish(url, next) {
  const entry = entryFor(url);
  const prev = entry.state;
  if (prev.playing === next.playing && prev.reason === next.reason && prev.confirmedAt === next.confirmedAt) {
    return;
  }
  entry.state = next;
  entry.updatedAt = Date.now();
  entry.listeners.forEach((listener) => listener());
}

// A verdict from any check - monitor cycle or force check. Only a "Yes" moves confirmedAt;
// a failure keeps the last confirmation time on screen.
function reportVerdict(url, playing, reason) {
  const confirmedAt = playing ? Date.now() : entryFor(url).state.confirmedAt;
  publish(url, { playing, reason, confirmedAt });
}

// The 30s monitor for one url. Every cycle proves four things in order - the master
// resolves, the variant playlist is valid/live, its timeline advances, and the CDN serves
// bytes of the newest segment - before the stream is reported as confirmed. A verdict is
// only ever downgraded by evidence: one failed fetch keeps the old answer until it fails
// twice, and when the stream comes back after an outage the stale failure drops back to
// "Checking" while the baseline is re-established (Checking -> Yes, never unreachable ->
// Yes). Decisions read the SHARED state, so a force check's verdict counts here too.
function startMonitor(src) {
  let cancelled = false;
  let currentUrl = src;
  let hops = 0;
  let fallbacks = 0;
  let prev = null;
  let failures = 0;
  let stalls = 0;
  let contentFailures = 0;

  const settle = (playing, reason) => {
    if (!cancelled) reportVerdict(src, playing, reason);
  };

  // Recovery: the manifest is served again but there is no baseline to compare against
  // yet. The failure reported during the outage is no longer true - show "Checking".
  const clearStaleVerdict = () => {
    if (cancelled) return;
    const { state } = entryFor(src);
    if (state.playing === false) publish(src, checkingState(state.confirmedAt));
  };

  // fromMaster: this cycle read the master, so the variant it probes is a fresh one.
  const probe = async (fromMaster = false) => {
    const text = await fetchText(currentUrl);
    if (cancelled) return;

    if (text == null) {
      failures += 1;
      // A variant URL is signed and expires - fall back to the master (bounded), which
      // hands out a fresh one, before calling the stream unreachable.
      if (hops > 0 && failures === 1 && fallbacks < MAX_MASTER_FALLBACKS) {
        fallbacks += 1;
        hops = 0;
        currentUrl = src;
        prev = null;
        await probe(true);
        return;
      }
      if (failures >= FAIL_LIMIT) settle(false, "unreachable");
      return;
    }
    failures = 0;

    const parsed = readManifestState(text, prev);
    if (parsed.status === "master") {
      if (hops >= MAX_VARIANT_HOPS) {
        settle(false, "invalid-manifest");
        return;
      }
      hops += 1;
      try {
        currentUrl = new URL(parsed.nextUrl, currentUrl).href;
      } catch {
        settle(false, "invalid-manifest");
        return;
      }
      prev = null; // a different playlist: restart the comparison
      await probe(true);
      return;
    }
    if (parsed.status === "ended") {
      // The variant kept from an earlier cycle can be a finished recording the master no
      // longer points at: before a broadcast, Mux's playback url serves the previous
      // recording (closed playlist), and once the new one is live the master hands out a
      // NEW signed variant. A kept variant re-resolves from the master before it counts -
      // otherwise this stays on the old recording ("Stream ended") while Go Live's check,
      // which always starts at the master, sees the stream playing.
      if (hops > 0 && !fromMaster) {
        hops = 0;
        currentUrl = src;
        prev = null;
        await probe(true);
        return;
      }
      settle(false, "ended");
      return;
    }
    if (parsed.status === "invalid") {
      settle(false, "invalid-manifest");
      return;
    }

    // sample / advancing / stalled - before reporting anything as healthy, prove the
    // CDN serves real bytes of the newest segment (see segmentHasContent above).
    const hasContent = await segmentHasContent(parsed.last, currentUrl);
    if (cancelled) return;
    if (!hasContent) {
      contentFailures += 1;
      if (contentFailures >= CONTENT_FAIL_LIMIT) settle(false, "no-content");
      return;
    }
    contentFailures = 0;

    if (parsed.status === "sample") {
      prev = { seq: parsed.seq, last: parsed.last };
      clearStaleVerdict();
      return;
    }
    if (parsed.status === "advancing") {
      prev = { seq: parsed.seq, last: parsed.last };
      stalls = 0;
      settle(true, "advancing");
      return;
    }
    stalls += 1;
    if (stalls >= STALL_LIMIT) {
      prev = { seq: parsed.seq, last: parsed.last };
      stalls = 0;
      settle(false, "stalled");
    }
  };

  probe();
  const timer = setInterval(() => probe(), PROBE_MS);
  return () => {
    cancelled = true;
    clearInterval(timer);
  };
}

function subscribe(url, listener) {
  const entry = entryFor(url);
  entry.listeners.add(listener);
  if (!entry.stopMonitor) {
    // A verdict nobody has refreshed for two cycles (a check run while no section showed
    // this url) is not the stream's current state: start the new viewer on "Checking".
    if (Date.now() - entry.updatedAt > TRUST_YES_MS) entry.state = checkingState();
    entry.stopMonitor = startMonitor(url);
  }
  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;
    entry.stopMonitor?.();
    entry.stopMonitor = null;
    // Nobody watches this url any more: the next viewer starts from a fresh first sample.
    entry.state = checkingState();
  };
}

// The shared playing status of a stream url (the Stream Health card).
export default function useStreamPlayback(src) {
  const subscribeToSrc = useCallback(
    (listener) => (src ? subscribe(src, listener) : () => {}),
    [src],
  );
  const getSnapshot = useCallback(() => (src ? entryFor(src).state : NO_URL), [src]);
  return useSyncExternalStore(subscribeToSrc, getSnapshot, getSnapshot);
}

// The on-demand "is this url playing?" check (Go Live's playing step). A current
// "Yes" - the one the Stream Health card is showing - answers at once, no network. Anything
// else runs the force probe, shared by concurrent callers, and publishes its verdict to
// every section. Resolves to { playing, reason }; never rejects.
export async function checkStreamPlaying(src, options) {
  if (!src) return { playing: false, reason: "no-url" };
  const entry = entryFor(src);
  const { state } = entry;
  if (state.playing === true && state.confirmedAt != null && Date.now() - state.confirmedAt <= TRUST_YES_MS) {
    return { playing: true, reason: state.reason };
  }
  if (!entry.forceCheck) {
    entry.forceCheck = probeStreamNow(src, options)
      .then((result) => {
        reportVerdict(src, result.playing, result.reason);
        return result;
      })
      .finally(() => {
        entry.forceCheck = null;
      });
  }
  return entry.forceCheck;
}

// Tests only: stop every monitor and forget every url.
export function __resetStreamPlaybackForTests() {
  for (const entry of entries.values()) entry.stopMonitor?.();
  entries.clear();
}
